import { useCallback, useEffect, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { RoomTransport } from "@games-monorepo/yak-trails-core";

import { YAK_TRAILS_CLIENT_TUNING } from "../tuning";

export function useSteeringControls(transport: RoomTransport | null, active: boolean, clientTick: number) {
  const anchor = useRef<{ x: number; y: number } | null>(null);
  const sequence = useRef(0);
  const directionRef = useRef<{ x: number; y: number } | null>(null);
  const lastSentAt = useRef(0);
  const keyState = useRef(new Set<string>());

  const send = useCallback((x: number, y: number, force = false) => {
    if (!transport || !active) return;
    const length = Math.hypot(x, y);
    if (length < YAK_TRAILS_CLIENT_TUNING.controls.directionDeadZone) return;
    const direction = { x: x / length, y: y / length };
    directionRef.current = direction;
    const now = performance.now();
    if (!force && now - lastSentAt.current < YAK_TRAILS_CLIENT_TUNING.controls.minimumSendIntervalMs) return;
    lastSentAt.current = now;
    transport.sendSteering({ seq: ++sequence.current, targetX: direction.x, targetY: direction.y, clientTick });
  }, [active, clientTick, transport]);

  useEffect(() => {
    if (!active) directionRef.current = null;
  }, [active]);

  useEffect(() => {
    const updateKeyboard = () => {
      let x = 0;
      let y = 0;
      if (keyState.current.has("arrowleft") || keyState.current.has("a")) x -= 1;
      if (keyState.current.has("arrowright") || keyState.current.has("d")) x += 1;
      if (keyState.current.has("arrowup") || keyState.current.has("w")) y -= 1;
      if (keyState.current.has("arrowdown") || keyState.current.has("s")) y += 1;
      if (x !== 0 || y !== 0) send(x, y, true);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      keyState.current.add(event.key.toLowerCase());
      updateKeyboard();
    };
    const onKeyUp = (event: KeyboardEvent) => {
      keyState.current.delete(event.key.toLowerCase());
      updateKeyboard();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [send]);

  return {
    directionRef,
    onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
      if (!active || !event.isPrimary) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      anchor.current = { x: event.clientX, y: event.clientY };
    },
    onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
      if (!active || !event.isPrimary || !anchor.current) return;
      send(event.clientX - anchor.current.x, event.clientY - anchor.current.y);
    },
    onPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
      if (!event.isPrimary) return;
      if (anchor.current) send(event.clientX - anchor.current.x, event.clientY - anchor.current.y, true);
      anchor.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onPointerCancel() {
      anchor.current = null;
    },
  };
}
